import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  Connection,
  EdgeChange,
  NodeChange,
  Panel,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  useReactFlow,
  MarkerType,
  useViewport
} from 'reactflow';
import 'reactflow/dist/style.css';
import { 
  ArrowLeft, 
  Trash2, 
  Save,
  Play,
  Pause,
  Settings,
  Plus,
  Minus,
  Maximize,
  RotateCcw,
  Unlink,
  AlertCircle,
  Check,
  Loader2
} from 'lucide-react';
import { SendMessageNode } from './nodes/SendMessageNode';
import { AskQuestionNode } from './nodes/AskQuestionNode';
import { BranchNode } from './nodes/BranchNode';
import { JumpNode } from './nodes/JumpNode';
import { HttpRequestNode } from './nodes/HttpRequestNode';
import { WaitForResponseNode } from './nodes/WaitForResponseNode';
import { TriggerNode } from './nodes/TriggerNode';
import { EndNode } from './nodes/EndNode';
import { NodeSidebar } from './NodeSidebar';
import { NodePalette } from './NodePalette';
import { saveFlow, updateFlow, activateFlow, deactivateFlow, deleteFlow, getFlow } from '../../lib/flows';

const nodeTypes = {
  sendMessage: SendMessageNode,
  askQuestion: AskQuestionNode,
  branch: BranchNode,
  jump: JumpNode,
  httpRequest: HttpRequestNode,
  waitForResponse: WaitForResponseNode,
  trigger: TriggerNode,
  end: EndNode,
};

const nodeColors = {
  trigger: '#3b82f6',
  sendMessage: '#10b981',
  askQuestion: '#6366f1',
  branch: '#f59e0b',
  jump: '#ef4444',
  httpRequest: '#8b5cf6',
  waitForResponse: '#ec4899',
  end: '#6B7280',
};

const defaultEdgeOptions = {
  type: 'smoothstep',
  animated: true,
  markerEnd: {
    type: MarkerType.ArrowClosed,
    width: 20,
    height: 20,
  },
  style: {
    stroke: '#64748b',
    strokeWidth: 2,
  },
};

interface ChatbotBuilderProps {
  instanceId: string;
  flowId: string;
  onBack: () => void;
}

export const ChatbotBuilder: React.FC<ChatbotBuilderProps> = ({ instanceId, flowId, onBack }) => {
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [flowName, setFlowName] = useState('Nuevo flujo');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNodeEditor, setShowNodeEditor] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [nodeFormData, setNodeFormData] = useState<any>({});
  const [savingNodeData, setSavingNodeData] = useState(false);
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);
  const { getEdges, deleteElements, zoomIn, zoomOut, fitView, getViewport } = useReactFlow();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => setNodes((nds) => applyNodeChanges(changes, nds)),
    []
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    []
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      if (isActive) return;
      setEdges((eds) => addEdge({
        ...connection,
        type: 'smoothstep',
        animated: true,
        style: { stroke: '#64748b', strokeWidth: 2 },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 20,
        },
      }, eds));
    },
    [isActive]
  );

  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    if (isActive) return;
    setSelectedNode(node);
    setNodeFormData(node.data);
    setShowNodeEditor(true);
  }, [isActive]);

  const onEdgeClick = useCallback((event: React.MouseEvent, edge: Edge) => {
    if (isActive) return;
    setSelectedEdge(edge);
  }, [isActive]);

  const onDisconnectNodes = useCallback(() => {
    if (!selectedEdge || isActive) return;
    deleteElements({ edges: [selectedEdge] });
    setSelectedEdge(null);
  }, [selectedEdge, isActive, deleteElements]);

  const handleZoomIn = () => {
    zoomIn();
  };

  const handleZoomOut = () => {
    zoomOut();
  };

  const handleFitView = () => {
    fitView({ padding: 0.2 });
  };

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      if (isActive) return;

      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
      const type = event.dataTransfer.getData('application/reactflow');

      if (typeof type === 'string' && reactFlowBounds) {
        const { zoom, x: viewportX, y: viewportY } = getViewport();
        const position = {
          x: (event.clientX - reactFlowBounds.left - viewportX) / zoom,
          y: (event.clientY - reactFlowBounds.top - viewportY) / zoom,
        };

        const newNode = {
          id: `${type}-${Date.now()}`,
          type,
          position,
          data: { 
            label: type,
            color: nodeColors[type as keyof typeof nodeColors],
            title: `Nuevo ${type}`,
            options: type === 'branch' ? [] : undefined
          }
        };

        setNodes((nds) => nds.concat(newNode));
      }
    },
    [isActive, getViewport]
  );

  useEffect(() => {
    if (flowId && flowId !== 'new') {
      loadFlow();
    } else {
      const triggerNode: Node = {
        id: `trigger-${Date.now()}`,
        type: 'trigger',
        position: { x: 100, y: 100 },
        data: { 
          label: 'Disparador',
          color: nodeColors.trigger,
          description: 'Se activa cuando se recibe un mensaje',
          title: 'Disparador Inicial'
        }
      };
      setNodes([triggerNode]);
      setLoading(false);
    }
  }, [flowId]);

  const loadFlow = async () => {
    try {
      const flow = await getFlow(flowId);
      if (flow) {
        setFlowName(flow.name);
        setNodes(flow.configuration.nodes);
        setEdges(flow.configuration.edges);
        setIsActive(flow.is_active);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!instanceId) return;
    
    try {
      setSaving(true);
      setError(null);

      const cleanedNodes = nodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          onEditOption: undefined
        }
      }));

      if (flowId === 'new') {
        await saveFlow(instanceId, flowName, cleanedNodes, edges);
      } else {
        await updateFlow(flowId, flowName, cleanedNodes, edges);
      }

      onBack();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    if (!instanceId) return;
    
    try {
      setUpdatingStatus(true);
      setError(null);
      
      if (isActive) {
        await deactivateFlow(instanceId);
        setIsActive(false);
      } else {
        await activateFlow(instanceId);
        setIsActive(true);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDelete = async () => {
    if (!flowId || flowId === 'new') return;
    
    if (!confirm('¿Estás seguro de que deseas eliminar este flujo?')) return;
    
    try {
      setSaving(true);
      setError(null);
      
      await deleteFlow(flowId);
      onBack();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNodeData = () => {
    if (!selectedNode) return;

    setSavingNodeData(true);
    try {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === selectedNode.id) {
            return {
              ...node,
              data: {
                ...node.data,
                ...nodeFormData,
                onEditOption: node.type === 'branch' ? (optionId: string) => {
                  setSelectedNode(node);
                  setNodeFormData(node.data);
                  setShowNodeEditor(true);
                } : undefined
              },
            };
          }
          return node;
        })
      );
      setShowNodeEditor(false);
      setSelectedNode(null);
      setNodeFormData({});
    } catch (err) {
      console.error('Error saving node data:', err);
    } finally {
      setSavingNodeData(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      <div className="h-16 bg-white border-b flex items-center justify-between px-8 shadow-sm">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Volver
          </button>
          <input
            type="text"
            value={flowName}
            onChange={(e) => setFlowName(e.target.value)}
            disabled={isActive}
            className="border-none focus:ring-0 text-lg font-medium text-gray-900 disabled:bg-transparent disabled:text-gray-500"
            placeholder="Nombre del flujo"
          />
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
            {isActive ? 'Activo' : 'Inactivo'}
          </span>
        </div>
        <div className="flex items-center space-x-4">
          {flowId !== 'new' && (
            <>
              <button
                onClick={handleToggleActive}
                disabled={saving || updatingStatus}
                className={`flex items-center px-4 py-2 text-white rounded-md disabled:opacity-50 ${
                  isActive 
                    ? 'bg-orange-500 hover:bg-orange-600'
                    : 'bg-green-500 hover:bg-green-600'
                }`}
              >
                {updatingStatus ? (
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                ) : isActive ? (
                  <Pause className="h-5 w-5 mr-2" />
                ) : (
                  <Play className="h-5 w-5 mr-2" />
                )}
                {isActive ? 'Desactivar Flujo' : 'Activar Flujo'}
              </button>
              <button
                onClick={handleDelete}
                disabled={saving || isActive}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-5 w-5 mr-2" />
                )}
                Eliminar Flujo
              </button>
            </>
          )}
          <button
            onClick={handleSave}
            disabled={saving || isActive}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            ) : (
              <Save className="h-5 w-5 mr-2" />
            )}
            Guardar Cambios
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border-l-4 border-red-400">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {isActive && (
        <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                El flujo está activo. Desactívalo para poder editarlo.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 flex">
        <NodePalette />

        <div 
          className="flex-1 bg-gray-50 relative" 
          ref={reactFlowWrapper}
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            nodeTypes={nodeTypes}
            defaultEdgeOptions={defaultEdgeOptions}
            onDragOver={onDragOver}
            onDrop={onDrop}
            fitView
            snapToGrid={false}
            snapGrid={[1, 1]}
            minZoom={0.1}
            maxZoom={4}
            defaultViewport={{ x: 0, y: 0, zoom: 1 }}
          >
            <Background />
            <Controls />
            <Panel position="bottom-center" className="bg-white p-2 rounded-t-lg shadow-lg border">
              <div className="flex items-center space-x-2">
                <button 
                  className="p-1 hover:bg-gray-100 rounded"
                  onClick={handleZoomIn}
                >
                  <Plus className="h-5 w-5 text-gray-600" />
                </button>
                <button 
                  className="p-1 hover:bg-gray-100 rounded"
                  onClick={handleZoomOut}
                >
                  <Minus className="h-5 w-5 text-gray-600" />
                </button>
                <button 
                  className="p-1 hover:bg-gray-100 rounded"
                  onClick={handleFitView}
                >
                  <Maximize className="h-5 w-5 text-gray-600" />
                </button>
                <button 
                  className="p-1 hover:bg-gray-100 rounded"
                  onClick={() => window.location.reload()}
                >
                  <RotateCcw className="h-5 w-5 text-gray-600" />
                </button>
                {selectedEdge && (
                  <button
                    onClick={onDisconnectNodes}
                    className="p-1 hover:bg-red-100 rounded text-red-600"
                    title="Desconectar nodos"
                  >
                    <Unlink className="h-5 w-5" />
                  </button>
                )}
              </div>
            </Panel>
          </ReactFlow>
        </div>

        {showNodeEditor && selectedNode && !isActive && (
          <NodeSidebar 
            node={selectedNode}
            formData={nodeFormData}
            setFormData={setNodeFormData}
            onClose={() => {
              setShowNodeEditor(false);
              setSelectedNode(null);
              setNodeFormData({});
            }}
            onSave={handleSaveNodeData}
            onDelete={() => {
              setNodes(nodes.filter(n => n.id !== selectedNode.id));
              setShowNodeEditor(false);
              setSelectedNode(null);
              setNodeFormData({});
            }}
            isSaving={savingNodeData}
            allNodes={nodes}
          />
        )}
      </div>
    </div>
  );
};